import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Container,
  MenuItem,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { api } from '../services/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const AddPaymentMethodForm = ({ clientSecret, customerEmail, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      toast.error(t('hardcoded.pages_billingportalpage.stripe_aun_no_esta_listo'));
      return;
    }

    setSubmitting(true);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: customerEmail || undefined,
          },
        },
      });

      if (error) {
        toast.error(error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_registrar_la'));
        return;
      }

      const paymentMethodId = typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id;

      if (setAsDefault && paymentMethodId) {
        await api.billing.setDefaultPaymentMethod(paymentMethodId);
      }

      toast.success(t('hardcoded.pages_billingportalpage.metodo_de_pago_agregado_correctamente'));
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_guardar_el'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
          <CardElement options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' },
              },
            },
          }} />
        </Box>

        <FormControlLabel
          control={(
            <Checkbox
              checked={setAsDefault}
              onChange={(event) => setSetAsDefault(event.target.checked)}
            />
          )}
          label={t('hardcoded.pages_billingportalpage.marcar_como_metodo_predeterminado')}
        />

        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" disabled={!stripe || submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : t('hardcoded.pages_billingportalpage.guardar_tarjeta')}
          </Button>
          <Button variant="text" onClick={onCancel} disabled={submitting}>{t('common.cancel')}</Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const BillingPortalPage = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [setupIntentSecret, setSetupIntentSecret] = useState('');
  const [preparingCardForm, setPreparingCardForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    amount: '',
    currency: 'MXN',
    payment_date: new Date().toISOString().slice(0, 10),
    reference: '',
    evidence: null,
  });

  const loadData = async () => {
    try {
      const [summaryResp, methodsResp] = await Promise.all([
        api.billing.getSelfSummary(),
        api.billing.listPaymentMethods(),
      ]);
      setSummary(summaryResp.data);
      setPaymentMethods(methodsResp.data?.payment_methods || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.error_cargando_portal_de_pagos'));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const ensureStripeCustomer = async () => {
    try {
      const response = await api.billing.createStripeCustomer();
      toast.success(t('hardcoded.pages_billingportalpage.customer_de_stripe_listo'));
      await loadData();
      return response?.data?.stripe_customer_id;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_crear_customer'));
      return null;
    }
  };

  const prepareInPageCardForm = async () => {
    if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      toast.error(t('hardcoded.pages_billingportalpage.falta_react_app_stripe_publishable_key_en_el_frontend'));
      return;
    }

    setPreparingCardForm(true);
    try {
      let stripeCustomerId = summary?.subscription?.stripe_customer_id;
      if (!stripeCustomerId) {
        stripeCustomerId = await ensureStripeCustomer();
      }

      if (!stripeCustomerId) {
        return;
      }

      const setupResp = await api.billing.createSetupIntent();
      const clientSecret = setupResp?.data?.client_secret;
      if (!clientSecret) {
        toast.error(t('hardcoded.pages_billingportalpage.no_se_pudo_preparar_el'));
        return;
      }

      setSetupIntentSecret(clientSecret);
      setShowCardForm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_iniciar_alta'));
    } finally {
      setPreparingCardForm(false);
    }
  };

  const openStripePortal = async () => {
    try {
      const response = await api.billing.openStripePortal();
      const url = response?.data?.url;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_abrir_stripe'));
    }
  };

  const submitManualPayment = async () => {
    if (!manualForm.amount || !manualForm.payment_date || !manualForm.evidence) {
      toast.error(t('hardcoded.pages_billingportalpage.completa_monto_fecha_y_evidencia'));
      return;
    }

    const form = new FormData();
    form.append('amount', manualForm.amount);
    form.append('currency', manualForm.currency);
    form.append('payment_date', manualForm.payment_date);
    form.append(t('hardcoded.pages_billingportalpage.reference'), manualForm.reference || '');
    form.append(t('hardcoded.pages_billingportalpage.evidence'), manualForm.evidence);

    try {
      await api.billing.createManualPayment(form);
      toast.success(t('hardcoded.pages_billingportalpage.pago_manual_enviado_para_validacion'));
      setManualForm({
        amount: '',
        currency: summary?.subscription?.billing_currency || 'MXN',
        payment_date: new Date().toISOString().slice(0, 10),
        reference: '',
        evidence: null,
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('hardcoded.pages_billingportalpage.no_se_pudo_enviar_el'));
    }
  };

  const status = summary?.subscription?.status || 'active';
  const statusColor = status === 'active' ? 'success' : (status === 'grace' ? 'warning' : 'error');

  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant="h4">{t('hardcoded.pages_billingportalpage.portal_de_cobranza')}</Typography>

        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6">{t('hardcoded.pages_billingportalpage.estado_de_suscripcion')}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={status.toUpperCase()} color={statusColor} />
                <Typography variant="body2">{i18n.t('hardcoded.pages_billingportalpage.moneda')}{summary?.subscription?.billing_currency || '-'}</Typography>
              </Stack>
              <Typography variant="body2">{t('hardcoded.pages_billingportalpage.razon')}{summary?.subscription?.reason || '-'}</Typography>
              <Typography variant="body2">{i18n.t('hardcoded.pages_billingportalpage.plan')}{summary?.plan?.name || '-'}</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('hardcoded.pages_billingportalpage.metodos_de_pago_automatico_stripe')}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button variant="contained" onClick={prepareInPageCardForm} disabled={preparingCardForm}>
                {preparingCardForm ? t('hardcoded.pages_billingportalpage.preparando') : t('hardcoded.pages_billingportalpage.agregar_metodo_aqui')}
              </Button>
              <Button variant="outlined" onClick={ensureStripeCustomer}>{t('hardcoded.pages_billingportalpage.crearverificar_customer')}</Button>
              <Button variant="text" onClick={openStripePortal}>{t('hardcoded.pages_billingportalpage.administrar_en_stripe_portal')}</Button>
            </Stack>

            {showCardForm && setupIntentSecret && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Registra tu tarjeta aquí mismo; al guardar se actualiza la lista sin salir de esta página.
                </Alert>
                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret }}>
                  <AddPaymentMethodForm
                    clientSecret={setupIntentSecret}
                    customerEmail={summary?.subscription?.billing_email}
                    onSuccess={async () => {
                      setShowCardForm(false);
                      setSetupIntentSecret('');
                      await loadData();
                    }}
                    onCancel={() => {
                      setShowCardForm(false);
                      setSetupIntentSecret('');
                    }}
                  />
                </Elements>
              </Box>
            )}

            {paymentMethods.length === 0 ? (
              <Alert severity="warning">{t('hardcoded.pages_billingportalpage.no_hay_metodos_de_pago')}</Alert>
            ) : (
              <Grid container spacing={2}>
                {paymentMethods.map((method) => (
                  <Grid item xs={12} md={6} key={method.id}>
                    <Paper
                      sx={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                        borderRadius: 2,
                        p: 2.5,
                        color: 'white',
                        boxShadow: '0 8px 32px rgba(124, 58, 237, 0.3)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: -50,
                          right: -50,
                          width: 200,
                          height: 200,
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '50%',
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Stack spacing={2}>
                          {/* Header with brand and actions */}
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <CreditCardIcon sx={{ fontSize: 32 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {method.card?.brand?.toUpperCase() || 'CARD'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => api.billing.setDefaultPaymentMethod(method.id).then(loadData)}
                                sx={{
                                  color: 'white',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                                }}
                              >
                                {method.is_default ? <StarIcon /> : <StarOutlineIcon />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => api.billing.detachPaymentMethod(method.id).then(loadData)}
                                sx={{
                                  color: 'white',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  '&:hover': { backgroundColor: 'rgba(255, 100, 100, 0.6)' },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </Stack>

                          {/* Card number section */}
                          <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1, p: 1.5 }}>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                              {t('hardcoded.pages_billingportalpage.numero_de_tarjeta')}</Typography>
                            <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: '2px', fontWeight: 600 }}>
                              •••• •••• •••• {method.card?.last4}
                            </Typography>
                          </Box>

                          {/* Expiration and Default status */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {i18n.t('hardcoded.components_tickets_ticketactioncenter.vence')}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {method.card?.exp_month}/{method.card?.exp_year}
                              </Typography>
                            </Stack>
                            {method.is_default && (
                              <Chip
                                label={t('hardcoded.pages_billingplanspage.predeterminado')}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  color: 'white',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {i18n.t('hardcoded.pages_billingportalpage.pago_manual_transferenciaefectivo')}</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('hardcoded.pages_billingportalpage.envia_tu_comprobante_de_pago')}</Alert>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label={t('hardcoded.pages_billinginvoicespage.monto')}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={manualForm.amount}
                    onChange={(e) => setManualForm((p) => ({ ...p, amount: e.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label={t('settings.company.currency')}
                    select
                    value={manualForm.currency}
                    onChange={(e) => setManualForm((p) => ({ ...p, currency: e.target.value }))}
                    variant="outlined"
                  >
                    <MenuItem value="MXN">MXN</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label={t('hardcoded.pages_billinginvoicespage.fecha_de_pago')}
                    type="date"
                    value={manualForm.payment_date}
                    onChange={(e) => setManualForm((p) => ({ ...p, payment_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('hardcoded.pages_billingportalpage.referencia_folioticket')}
                    value={manualForm.reference}
                    onChange={(e) => setManualForm((p) => ({ ...p, reference: e.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', textAlign: 'center', border: '2px dashed #ccc' }}>
                    <input
                      hidden
                      id="evidence-upload"
                      type="file"
                      onChange={(e) => setManualForm((p) => ({ ...p, evidence: e.target.files?.[0] || null }))}
                    />
                    <label htmlFor="evidence-upload" style={{ cursor: 'pointer', display: 'block' }}>
                      <Stack spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {manualForm.evidence?.name ? '✓ ' : '📄 '}
                          {manualForm.evidence?.name || t('hardcoded.pages_billingportalpage.haz_clic_para_subir_comprobante')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {t('hardcoded.pages_billingportalpage.pdf_jpg_o_png_-')}</Typography>
                      </Stack>
                    </label>
                  </Paper>
                </Grid>
              </Grid>

              <Divider />
              <Button
                variant="contained"
                size="large"
                onClick={submitManualPayment}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('hardcoded.pages_billingportalpage.enviar_comprobante_para_validacion')}</Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default BillingPortalPage;
